'use client'
import { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react'
import { cn } from '../../helpers/utils.js'
import { withLayout } from '../../shared/Styles.js'
import { Button, Text } from '../../components/index.js'
import { useExposure } from '../../hooks/useExposure.js'
import type { ProductNavProps } from './types.js'
import * as Popover from '@radix-ui/react-popover'

const componentType = 'navigation'
const componentName = 'product_nav'

const ChevronDownIcon = (props: React.SVGProps<SVGSVGElement>) => {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path
        d="M5 7.5L10 12.5L15 7.5"
        stroke="currentColor"
        strokeWidth="1.66667"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

const ProductNav = forwardRef<HTMLDivElement, ProductNavProps>(
  ({ className = '', data, id, onAnchorClick, scrollOffsetExtra = 0 }, ref) => {
    const { tabLinks = [], anchorList = [], textLink, buyBtn, theme = 'light' } = data || {}
    const [activeAnchorId, setActiveAnchorId] = useState('')
    const innerRef = useRef<HTMLDivElement>(null)
    const isScrollingRef = useRef(false) // 标记是否正在程序化滚动中
    const targetAnchorIdRef = useRef<string>('') // 记录点击目标锚点
    const scrollEndTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null) // 滚动结束检测定时器
    const anchorPositionsRef = useRef<Map<string, number>>(new Map()) // 缓存锚点原始位置

    useImperativeHandle(ref, () => innerRef.current as HTMLDivElement)

    useExposure(innerRef, {
      componentType,
      componentName,
    })

    // 初始化时缓存所有锚点的原始位置（在滚动前计算，避免 sticky 影响）
    useEffect(() => {
      if (anchorList.length === 0) return

      // 页面加载后延迟计算，确保布局完成
      const timer = setTimeout(() => {
        // 先滚动到顶部计算真实位置
        const originalScrollY = window.scrollY
        window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior })

        // 等待一帧让布局稳定
        requestAnimationFrame(() => {
          const positions = new Map<string, number>()
          anchorList.forEach(anchor => {
            const element = document.getElementById(anchor.id)
            if (element) {
              const rect = element.getBoundingClientRect()
              positions.set(anchor.id, rect.top + window.scrollY)
            }
          })
          anchorPositionsRef.current = positions

          // 恢复原来的滚动位置
          window.scrollTo({ top: originalScrollY, behavior: 'instant' as ScrollBehavior })
        })
      }, 100)

      return () => clearTimeout(timer)
    }, [anchorList])

    // 滚动监听：检测当前滚动位置对应的锚点
    useEffect(() => {
      if (anchorList.length === 0) return

      const handleScroll = () => {
        // 如果正在程序化滚动中，使用 debounce 检测滚动结束
        if (isScrollingRef.current) {
          // 清除之前的定时器
          if (scrollEndTimerRef.current) {
            clearTimeout(scrollEndTimerRef.current)
          }
          // 设置新的定时器，100ms 没有新滚动事件则认为滚动结束
          scrollEndTimerRef.current = setTimeout(() => {
            isScrollingRef.current = false
            // 滚动结束后，确保高亮停留在目标锚点
            if (targetAnchorIdRef.current) {
              setActiveAnchorId(targetAnchorIdRef.current)
              targetAnchorIdRef.current = ''
            }
          }, 100)
          return
        }

        const navHeight = innerRef.current?.offsetHeight || 0
        const scrollPosition = window.scrollY + navHeight + scrollOffsetExtra + 10 // 10px 缓冲

        let currentAnchorId = ''

        // 使用缓存的锚点位置
        const positions = anchorPositionsRef.current

        // 遍历所有锚点，找到当前滚动位置对应的锚点
        for (let i = anchorList.length - 1; i >= 0; i--) {
          const anchor = anchorList[i]
          const elementTop = positions.get(anchor.id)
          if (elementTop !== undefined && scrollPosition >= elementTop) {
            currentAnchorId = anchor.id
            break
          }
        }

        // 如果没有找到且缓存中有第一个锚点的位置
        if (!currentAnchorId && anchorList.length > 0) {
          const firstElementTop = positions.get(anchorList[0].id)
          if (firstElementTop !== undefined && scrollPosition < firstElementTop) {
            currentAnchorId = ''
          }
        }

        setActiveAnchorId(currentAnchorId)
      }

      // 延迟初始化，等待位置缓存完成
      const timer = setTimeout(() => {
        handleScroll()
      }, 200)

      window.addEventListener('scroll', handleScroll, { passive: true })
      return () => {
        clearTimeout(timer)
        window.removeEventListener('scroll', handleScroll)
        // 清理定时器
        if (scrollEndTimerRef.current) {
          clearTimeout(scrollEndTimerRef.current)
        }
      }
    }, [anchorList, scrollOffsetExtra])

    // 设置页面中header元素的position为relative
    useEffect(() => {
      const headerElements = document.querySelectorAll('.header')
      const originalStyles: { element: HTMLElement; originalPosition: string }[] = []

      headerElements.forEach(element => {
        const htmlElement = element as HTMLElement
        // 保存原始样式
        originalStyles.push({
          element: htmlElement,
          originalPosition: htmlElement.style.position || getComputedStyle(htmlElement).position,
        })
        // 设置为relative
        htmlElement.style.position = 'relative'
      })

      // 清理函数：恢复原始样式
      return () => {
        originalStyles.forEach(({ element, originalPosition }) => {
          element.style.position = originalPosition === 'static' ? '' : originalPosition
        })
      }
    }, [])

    // 处理锚点点击滚动
    const handleAnchorClick = (e: React.MouseEvent, anchorId: string) => {
      e.preventDefault()

      // 使用缓存的位置
      const elementAbsoluteTop = anchorPositionsRef.current.get(anchorId)
      if (elementAbsoluteTop === undefined) {
        console.warn(`Cached position for "${anchorId}" not found`)
        return
      }

      // 立即更新高亮状态，避免滚动过程中闪烁
      setActiveAnchorId(anchorId)

      // 记录目标锚点，用于滚动结束后确保高亮正确
      targetAnchorIdRef.current = anchorId

      // 标记正在程序化滚动，暂停滚动监听
      isScrollingRef.current = true

      // 获取导航栏高度
      const navHeight = innerRef.current?.offsetHeight || 0

      // 目标滚动位置 = 缓存的元素绝对位置 - 导航栏高度 - 额外偏移
      const targetPosition = elementAbsoluteTop - navHeight - scrollOffsetExtra

      // 平滑滚动到目标位置
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth',
      })

      // 更新 URL hash（不触发页面跳转）
      window.history.pushState(null, '', `#${anchorId}`)

      // 注意：滚动结束检测由 handleScroll 中的 debounce 逻辑处理
      // 不再使用固定 800ms 超时，而是检测实际滚动结束
    }

    return (
      <div
        id={id}
        ref={innerRef}
        className={cn(
          'product-nav text-info-primary bg-container-primary tablet:px-8 laptop:px-16 desktop:px-16 lg-desktop:px-[calc(50%-832px)] !sticky top-0 !z-[51] flex w-full justify-between overflow-hidden px-4 py-0',
          {
            'aiui-dark': theme === 'dark',
          },
          className
        )}
      >
        <div className="product-nav-main desktop:flex-row desktop:gap-[48px] flex w-full flex-col justify-start">
          {/* Tab Links Section */}
          <div className="product-nav-header laptop:w-full desktop:w-fit desktop:gap-[24px] desktop:py-0 flex w-full flex-row items-center justify-between gap-[16px] pt-[14px]">
            <div className="product-nav-tabs flex flex-row items-center gap-[8px]">
              {tabLinks.map((tabLink, index) => {
                return (
                  <div key={tabLink.link || index} className="product-nav-tab-item flex items-center gap-[8px]">
                    <a
                      href={tabLink.link}
                      className={cn(
                        'product-nav-tab-link hover:text-brand-color text-nowrap text-[14px] font-bold tracking-[-0.04em]',
                        {
                          'text-[#6D6D6F]': index !== 0,
                          'hidden desktop:block': index === 1,
                        }
                      )}
                      // onClick={() => onShowSpecs?.(false)}
                    >
                      <span>{tabLink.label}</span>
                    </a>
                    {index !== tabLinks.length - 1 && (
                      <div className="product-nav-tab-divider desktop:inline-block hidden h-[14px] w-px bg-[#E4E5E6]" />
                    )}
                  </div>
                )
              })}

              {/* Mobile Dropdown */}
              {tabLinks.length > 1 && (
                <Popover.Root>
                  <Popover.Trigger asChild>
                    <button
                      className="product-nav-dropdown-trigger desktop:hidden -ml-1 inline-block"
                      aria-label="More products"
                    >
                      <ChevronDownIcon className="text-[#1D1D1F]" />
                    </button>
                  </Popover.Trigger>
                  <Popover.Content
                    className="product-nav-dropdown-content rounded-[6px] border border-[#E4E5E6] bg-white p-[6px] px-[8px] py-[4px]"
                    style={{
                      boxShadow: '0px 4px 8px 0px rgba(0,0,0,0.12)',
                    }}
                    side="bottom"
                    align="end"
                    alignOffset={10}
                    sideOffset={10}
                  >
                    {tabLinks.slice(1).map(tabLink => (
                      <a
                        key={tabLink.link}
                        href={tabLink.link}
                        className="product-nav-dropdown-link hover:text-brand-color text-nowrap"
                      >
                        <span>{tabLink.label}</span>
                      </a>
                    ))}
                  </Popover.Content>
                </Popover.Root>
              )}
            </div>

            {/* Mobile Actions */}
            <div className="product-nav-mobile-actions desktop:hidden desktop:gap-[24px] flex items-center gap-[16px]">
              {/* {specs?.text && (
                <Text
                  size={2}
                  onClick={handleSpecsClick}
                  className="product-nav-specs-button cursor-pointer hover:text-brand-color"
                >
                  {specs.text}
                </Text>
              )} */}
              {textLink?.link && (
                <a
                  href={textLink.link}
                  target={textLink.target || '_self'}
                  className="product-nav-text-link hover:text-brand-color cursor-pointer"
                >
                  <Text size={2} className="text-nowrap">
                    {textLink.text}
                  </Text>
                </a>
              )}
              {buyBtn?.link && (
                <Button as="a" href={buyBtn.link} size="sm" className="product-nav-buy-button text-nowrap">
                  {buyBtn.text}
                </Button>
              )}
            </div>
          </div>

          {/* Anchor Navigation */}
          <div className="product-nav-anchors laptop:w-fit desktop:gap-[24px] desktop:py-[8px] flex w-full items-center gap-[16px] overflow-x-scroll py-[12px] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {anchorList.map((anchor, index) => (
              <a
                key={anchor.id}
                href={`#${anchor.id}`}
                className="product-nav-anchor-link"
                onClick={e => {
                  // 如果提供了自定义点击事件,使用自定义事件并阻止默认行为
                  if (onAnchorClick) {
                    e.preventDefault()
                    // 标记程序化滚动，避免滚动过程中高亮闪烁
                    setActiveAnchorId(anchor.id)
                    targetAnchorIdRef.current = anchor.id
                    isScrollingRef.current = true
                    onAnchorClick(anchor, index)
                  } else {
                    // 使用默认的锚点滚动行为，带偏移量
                    handleAnchorClick(e, anchor.id)
                  }
                }}
              >
                <Text size={2} className="product-nav-anchor-text text-nowrap">
                  {anchor.label}
                </Text>
                <div
                  className={cn(
                    'product-nav-anchor-indicator laptop:top-[12px] desktop:top-[10px] relative top-[12px] h-[4px] w-full bg-transparent',
                    {
                      '!bg-brand-0': activeAnchorId === anchor.id,
                    }
                  )}
                />
              </a>
            ))}
          </div>
        </div>

        {/* Desktop Actions */}
        <div className="product-nav-desktop-actions desktop:flex desktop:gap-[24px] desktop:py-[8px] hidden items-center gap-[16px]">
          {/* {specs?.text && (
            <Text
              size={2}
              onClick={handleSpecsClick}
              className="product-nav-specs-button cursor-pointer text-black hover:text-brand-color"
            >
              {specs.text}
            </Text>
          )} */}
          {textLink?.link && (
            <a
              href={textLink.link}
              target={textLink.target || '_self'}
              className="product-nav-text-link hover:text-brand-color cursor-pointer text-black"
            >
              <Text size={2} className="text-nowrap">
                {textLink.text}
              </Text>
            </a>
          )}
          {buyBtn?.link && (
            <Button as="a" href={buyBtn.link} size="sm" className="product-nav-buy-button text-nowrap">
              {buyBtn.text}
            </Button>
          )}
        </div>
      </div>
    )
  }
)

ProductNav.displayName = 'ProductNav'

export default withLayout(ProductNav)
